import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, TrendingUp, TrendingDown, Minus, Package, Building2, History, AlertTriangle } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PriceHistoryPopup({ 
  open, 
  onClose, 
  itemId, 
  itemName,
  supplierId,
  supplierName,
  currentPrice,
  unit
}) {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && itemId) {
      fetchPriceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itemId, supplierId, currentPrice]);

  const fetchPriceHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (supplierId) params.append('supplier_id', supplierId);
      if (supplierName) params.append('supplier_name', supplierName);
      if (currentPrice) params.append('current_price', currentPrice);
      
      const response = await axios.get(
        `${API_URL}/store/price-history/${itemId}?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setData(response.data);
    } catch (err) {
      console.error('Error fetching price history:', err);
      setError('Failed to load price history');
    } finally {
      setLoading(false);
    }
  };

  const getVarianceIcon = (color) => {
    if (color === 'green') return <TrendingDown className="w-4 h-4" />;
    if (color === 'red') return <TrendingUp className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getVarianceBadgeClass = (color) => {
    switch (color) {
      case 'green': return 'bg-green-100 text-green-700 border-green-200';
      case 'yellow': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'red': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getComparisonSourceLabel = (source) => {
    switch (source) {
      case 'last_same_supplier': return 'vs Last Same Supplier';
      case 'avg_all_suppliers': return 'vs Avg All Suppliers';
      case 'opening_stock': return 'vs Opening Stock';
      default: return '';
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return `₹${price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Price History: {itemName || data?.item_name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            {error}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current PO Price */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-700 font-medium">Current PO Price</span>
                  <span className="text-xl font-bold text-blue-900">
                    {formatPrice(currentPrice)}/{unit || data?.item_unit}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Variance Alert */}
            {data?.variance && (
              <Card className={`border-2 ${
                data.variance_color === 'red' ? 'border-red-300 bg-red-50' :
                data.variance_color === 'yellow' ? 'border-yellow-300 bg-yellow-50' :
                'border-green-300 bg-green-50'
              }`}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      {getVarianceIcon(data.variance_color)}
                      <span className="font-medium">Variance</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={getVarianceBadgeClass(data.variance_color)}>
                        {data.variance.percent > 0 ? '+' : ''}{data.variance.percent}%
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        ({formatPrice(data.variance.amount)}) {getComparisonSourceLabel(data.variance.comparison_source)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Last Price (Same Supplier) */}
            {(supplierId || supplierName) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Last Price (Same Supplier - {supplierName})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {data?.last_same_supplier ? (
                    <div className="space-y-1">
                      <div className="text-lg font-bold">
                        {formatPrice(data.last_same_supplier.price)}/{data.last_same_supplier.unit || unit}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        on {data.last_same_supplier.date ? formatDate(data.last_same_supplier.date) : '-'} 
                        {data.last_same_supplier.grn_no && ` (GRN: ${data.last_same_supplier.grn_no})`}
                        {data.last_same_supplier.po_number && ` | PO: ${data.last_same_supplier.po_number}`}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Qty: {data.last_same_supplier.quantity} {data.last_same_supplier.unit || unit}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground italic">
                      No previous purchases from this supplier
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Opening Stock Price */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Opening Stock Price
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data?.opening_stock_price ? (
                  <div className="text-lg font-bold">
                    {formatPrice(data.opening_stock_price)}/{unit || data?.item_unit}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">
                    No opening stock price available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Average Price (All Suppliers) */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Average Price (All Suppliers)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data?.avg_all_suppliers ? (
                  <div className="space-y-1">
                    <div className="text-lg font-bold">
                      {formatPrice(data.avg_all_suppliers.price)}/{unit || data?.item_unit}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      from {data.avg_all_suppliers.transaction_count} transaction(s)
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">
                    No purchase history available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Opening Stock Price */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Opening Stock Price
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data?.opening_stock_price ? (
                  <div className="text-lg font-bold">
                    {formatPrice(data.opening_stock_price)}/{unit || data?.item_unit}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">
                    No opening stock price set
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Transactions */}
            {data?.recent_transactions && data.recent_transactions.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Recent Transactions (Last 5)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>GRN</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recent_transactions.map((txn, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">
                            {txn.date ? formatDate(txn.date) : '-'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {txn.supplier_name}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatPrice(txn.price)}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {txn.quantity} {txn.unit}
                          </TableCell>
                          <TableCell className="text-sm font-mono">
                            {txn.grn_no || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* No History Message */}
            {!data?.has_history && (
              <Card className="bg-gray-50">
                <CardContent className="pt-6 text-center">
                  <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-muted-foreground">
                    New Item - No price history available
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
